import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Landing page mappings for different user roles
 * Used to redirect users to appropriate pages when access is denied
 */
const landingPages = {
  admin: '/',                    // Procurement Dashboard
  transport_manager: '/bids',    // Transporter Bids
  transport_staff: '/bids',      // Transporter Bids
  procurement_manager: '/procurement-bids',  // Procurement Bids
  procurement_staff: '/procurement-bids',    // Procurement Bids
};

/**
 * ProtectedRoute Component
 * 
 * Provides role-based access control for application routes.
 * Redirects unauthenticated users to login and unauthorized users to their landing page.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactElement} props.element - The component to render if access is granted
 * @param {string[]} props.allowedRoles - Array of roles that can access this route
 * @returns {React.ReactElement} The protected component or redirect
 */
export default function ProtectedRoute({ element, allowedRoles = [] }) {
  const { currentUser, userRole } = useContext(AuthContext);

  // Redirect to login if user is not authenticated
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Show loading spinner while checking user role
  if (userRole === null) {
    return (
      <div style={{ textAlign: 'center', marginTop: 80 }}>
        <Spin size="large" tip="Checking permissions…" />
      </div>
    );
  }

  // Check if user has required role for this route
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    // Redirect to role-appropriate landing page
    const home = landingPages[userRole] || '/login';
    return <Navigate to={home} replace />;
  }

  // Access granted - render the protected component
  return element;
}
